/**
 * Money for the Outlook add-in's catering view-model layer.
 *
 * This is deliberately simpler than the vendor `Money` type carried in
 * `apps/api/src/order-iss/models/money.ts`: the vendor value is currency-less
 * integer minor units, resolved only at `orders/validate` time. This `Price`
 * adds a currency at the app layer (see build-order spec decision #8 -
 * currency defaults to GBP via config) so it can be displayed directly.
 *
 * `OrderQuote` totals are always read from the service (mock today, the real
 * `orders/validate` response later) and never computed by summing UI state -
 * the functions below exist to assemble service-side mock fixtures and to
 * render a `Price` for display, not to let a page compute its own total.
 */

import type { Price } from '../types/catering';

const DEFAULT_LOCALE = 'en-GB';

/** A zero-value price in the given currency. Useful as a `reduce` seed. */
export function zeroPrice(currency: string): Price {
  return { minorUnits: 0, currency };
}

/**
 * Adds two prices in the same currency.
 *
 * @throws Error when the currencies differ, rather than silently mixing them.
 */
export function addPrices(a: Price, b: Price): Price {
  if (a.currency !== b.currency) {
    throw new Error(
      `Cannot add prices in different currencies: "${a.currency}" and "${b.currency}".`,
    );
  }

  return { minorUnits: a.minorUnits + b.minorUnits, currency: a.currency };
}

/**
 * Scales a price by a factor (e.g. portions x headcount, or a VAT rate).
 * Minor units are always an integer, so the result is rounded to the nearest
 * whole minor unit rather than left fractional.
 */
export function multiplyPrice(price: Price, factor: number): Price {
  return { minorUnits: Math.round(price.minorUnits * factor), currency: price.currency };
}

/**
 * Renders a price for display. Falls back to a plain decimal string with the
 * currency code when the currency is not a recognized ISO 4217 code, rather
 * than letting `Intl.NumberFormat` throw and blank out the UI.
 */
export function formatPrice(price: Price, locale: string = DEFAULT_LOCALE): string {
  const amount = price.minorUnits / 100;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: price.currency,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${price.currency}`;
  }
}

export function formatAmount(price: Price): string {
  return (price.minorUnits / 100).toFixed(2);
}

export function formatUnitAmount(amount: number, currency = 'GBP'): string {
  try {
    return new Intl.NumberFormat(DEFAULT_LOCALE, {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}
